'use client';

import React from 'react';
import {
    TbBuildingMonument, TbTrees, TbMasksTheater, TbBuildingCastle,
    TbGlassFull, TbTrekking, TbSailboat, TbCompass,
} from 'react-icons/tb';
import AffiliateLink from '@/components/common/AffiliateLink';
import { decorateTripsterUrl } from '@/lib/affiliates/utils/tripster';
import { t as tt } from '@/i18n/translations';

/**
 * Карточка тура в стиле Gallery·Field (`.to`), без фото. Используется в секции
 * «Экскурсии и туры» на странице города. Данные — документ типа `tour`
 * (см. src/sanity/queries/city.js, импорт из Tripster/Viator).
 */

// Иконка по категории (Tabler, как у сервисных карточек). Первая категория
// тура задаёт иконку-бейдж карточки; те же иконки — в чипах фильтра.
export const TOUR_CATEGORY_ICONS = {
    sightseeing: TbBuildingMonument,
    nature: TbTrees,
    culture: TbMasksTheater,
    history: TbBuildingCastle,
    food: TbGlassFull,
    adventure: TbTrekking,
    boat_tours: TbSailboat,
};
export const DEFAULT_TOUR_ICON = TbCompass;

// key → [ru, en]
export const TOUR_CATEGORY_LABELS = {
    sightseeing: ['Обзорная', 'Sightseeing'],
    nature: ['Природа', 'Nature'],
    culture: ['Культура', 'Culture'],
    history: ['История', 'History'],
    food: ['Еда и вино', 'Food & wine'],
    adventure: ['Приключения', 'Adventure'],
    boat_tours: ['Морская прогулка', 'Boat tour'],
};

export const GROUP_TYPE_LABELS = {
    group: ['Групповой', 'Group'],
    small_group: ['Мини-группа', 'Small group'],
    private: ['Индивидуальный', 'Private'],
};

const SOURCE_LABELS = { tripster: 'Tripster', viator: 'Viator' };

/** Минуты → «10 ч» / «4 ч 30 мин» (ru), "10h" / "4h 30m" (en); ui = ui.card */
export function fmtDuration(minutes, ui) {
    if (typeof minutes !== 'number' || minutes <= 0) return null;
    const h = Math.floor(minutes / 60);
    const m = minutes % 60;
    return [h > 0 ? ui.durationHours(h) : null, m > 0 ? ui.durationMinutes(m) : null].filter(Boolean).join(' ');
}

/** Цена «от …» с валютой источника, округляется до целого; ui = ui.card */
export function fmtPriceFrom(price, currency, ui) {
    if (typeof price !== 'number' || price <= 0) return null;
    const value = String(Math.round(price));
    const symbol = currency === 'USD' ? '$' : '€';
    return ui.priceFrom(value, symbol);
}

export default function TourCard({ tour: t, lang = 'ru', countrySlug, citySlug, position = 'city-tour' }) {
    const isRu = lang === 'ru';
    const ui = tt(lang, 'toursWidgetsUi').card;

    // Tripster декорируем партнёрскими параметрами; Viator-ссылки приходят
    // из выгрузки уже с pid/mcid.
    const href = t.source === 'tripster'
        ? decorateTripsterUrl(t.url, `${citySlug || ''}-tour-${t.extId || t._id}`)
        : t.url;

    const duration = fmtDuration(t.durationMinutes, ui);
    const group = GROUP_TYPE_LABELS[t.groupType]?.[isRu ? 0 : 1];
    const inSiteLang = Array.isArray(t.languages) && t.languages.includes(lang);
    const langLabel = inSiteLang ? ui.inLang : null;
    const metaParts = [duration, group, langLabel].filter(Boolean);

    const chips = (t.categories || [])
        .map((c) => TOUR_CATEGORY_LABELS[c]?.[isRu ? 0 : 1])
        .filter(Boolean)
        .slice(0, 3);
    if (t.childFriendly && chips.length < 3) chips.push(ui.kidFriendly);

    const price = fmtPriceFrom(t.priceFrom, t.currency, ui);

    const Icon = TOUR_CATEGORY_ICONS[(t.categories || [])[0]] || DEFAULT_TOUR_ICON;

    return (
        <article className="to">
            <div className="to-top">
                <span className="to-id">
                    <span className="to-ic" aria-hidden><Icon /></span>
                    <span className="to-src">{SOURCE_LABELS[t.source] || t.source}</span>
                </span>
                {typeof t.rating === 'number' && t.rating > 0 && (
                    <span className="to-score" title={t.reviewCount ? `${t.reviewCount} ${ui.reviews}` : undefined}>
                        ★ {t.rating.toFixed(1)}
                        {t.reviewCount ? <em>({t.reviewCount})</em> : null}
                    </span>
                )}
            </div>

            <h3>{t.title}</h3>
            {metaParts.length > 0 && <span className="to-meta">{metaParts.join(' · ')}</span>}
            {t.tagline && <p className="to-ex">{t.tagline}</p>}

            <div className="to-bottom">
                {chips.length > 0 && (
                    <ul className="to-am">
                        {chips.map((c) => <li key={c}>{c}</li>)}
                    </ul>
                )}
                <div className="to-cta">
                    {price && <span className="to-price">{price}</span>}
                    {href && (
                        <AffiliateLink href={href} provider={t.source} vertical="tours" country={countrySlug} position={position} className="to-btn">
                            {ui.view}
                        </AffiliateLink>
                    )}
                </div>
            </div>
        </article>
    );
}
